import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { formatNumberFixed, formatSignedPercentage } from "../../../utils/numberUtils";
import {
  nearestActiveDays,
  type BaselineDay,
  type MoveContext,
} from "../../../utils/strategyMoveContext";
import StrategyMoveContextExplainer from "./StrategyMoveContextExplainer";

interface Props {
  moveContext: MoveContext;
  dayLabel: string; // "today" or a formatted date
  onClose: () => void;
}

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatDayLabel(iso: string): string {
  // iso = "YYYY-MM-DD"; format without Date() to avoid timezone shifts.
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${MON[m - 1]} ${d}`;
}

// Stable pseudo-random in [0,1) from an index (no Math.random, so dots don't
// jump on re-render).
function jitter(i: number): number {
  const x = Math.sin((i + 1) * 127.1) * 43758.5453;
  return x - Math.floor(x);
}

// Round SVG coordinates to 1dp (avoids .toFixed per repo lint rule).
function coord(n: number): number {
  return Math.round(n * 10) / 10;
}

const CHART_W = 640;
const CHART_H = 320;
const PAD = 26;
const BASE_Y = CHART_H - 40;
const TOP = 24;

export default function StrategyMoveContextModal({ moveContext, dayLabel, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const te = moveContext.trackingErrorPct ?? 0;
  const today = moveContext.activeDayPct;
  const [view, setView] = useState<"how" | "detail">("how");

  const geom = useMemo(() => {
    if (!(te > 0)) return null;
    const xMin = -3.3 * te;
    const xMax = 3.3 * te;
    const sx = (x: number) =>
      PAD + ((Math.max(xMin, Math.min(xMax, x)) - xMin) / (xMax - xMin)) * (CHART_W - 2 * PAD);
    const f = (x: number) => Math.exp(-0.5 * (x / te) ** 2);
    const sy = (v: number) => BASE_Y - v * (BASE_Y - TOP);

    let curve = "";
    for (let x = xMin; x <= xMax + 1e-6; x += (xMax - xMin) / 160) {
      curve += (curve ? " L" : "M") + ` ${coord(sx(x))} ${coord(sy(f(x)))}`;
    }
    let band = `M ${coord(sx(-te))} ${BASE_Y}`;
    for (let x = -te; x <= te + 1e-6; x += te / 16) band += ` L ${coord(sx(x))} ${coord(sy(f(x)))}`;
    band += ` L ${coord(sx(te))} ${BASE_Y} Z`;

    const dots = moveContext.activeReturns.map((day, i) => {
      const x = sx(day.activePct);
      const yTop = sy(f(day.activePct));
      return { x, y: BASE_Y - jitter(i) * (BASE_Y - yTop), date: day.date, activePct: day.activePct };
    });

    const ticks = [-2 * te, -te, 0, te, 2 * te].map((x) => ({ x: sx(x), label: formatSignedPercentage(x, 1) }));
    const todayX = today != null ? sx(today) : null;
    const todayY = today != null ? sy(f(today)) : null;
    return { curve, band, dots, ticks, todayX, todayY };
  }, [te, today, moveContext.activeReturns]);

  const nearest: BaselineDay[] = useMemo(
    () => (today != null ? nearestActiveDays(moveContext.activeReturns, today, 5) : []),
    [today, moveContext.activeReturns]
  );
  const nearestSet = useMemo(() => new Set(nearest.map((n) => n.date)), [nearest]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Where the gap sits"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-5xl max-h-[92vh] overflow-auto rounded-2xl border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface p-5 md:p-7">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white">
              Where {dayLabel === "today" ? "today's" : `${dayLabel}'s`} gap sits
            </h3>
            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
              {view === "how"
                ? "A quick walkthrough of how this is built from the strategy's own history."
                : "Each dot is one past day's gap vs the benchmark. Together they form your expected range."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-rp-surface-raised"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {geom && (
          <div className="mt-3 inline-flex rounded-lg border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface p-0.5">
            <button
              type="button"
              onClick={() => setView("how")}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${view === "how" ? "bg-rp-amber text-white" : "text-gray-500 dark:text-gray-400"}`}
            >
              How it works
            </button>
            <button
              type="button"
              onClick={() => setView("detail")}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${view === "detail" ? "bg-rp-amber text-white" : "text-gray-500 dark:text-gray-400"}`}
            >
              Where it sits
            </button>
          </div>
        )}

        {!geom ? (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Not enough history to chart the range yet.</p>
        ) : view === "how" ? (
          <div className="mt-4">
            <StrategyMoveContextExplainer moveContext={moveContext} dayLabel={dayLabel} />
          </div>
        ) : (
          <>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_15rem]">
            <div>
              <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="w-full h-auto" role="img" aria-label="Distribution of daily active returns">
                <path d={geom.band} className="fill-green-500/10" />
                <line x1={PAD} x2={CHART_W - PAD} y1={BASE_Y} y2={BASE_Y} className="stroke-gray-200 dark:stroke-gray-700" strokeWidth={1} />
                <path d={geom.curve} fill="none" className="stroke-gray-400 dark:stroke-gray-500" strokeWidth={1.5} />
                {geom.dots.map((d, i) => {
                  const near = nearestSet.has(d.date);
                  return (
                    <circle
                      key={i}
                      cx={coord(d.x)}
                      cy={coord(d.y)}
                      r={near ? 5 : 3}
                      className={near ? "fill-rp-amber" : "fill-gray-400 dark:fill-gray-500"}
                      opacity={near ? 1 : 0.5}
                    />
                  );
                })}
                {geom.todayX != null && geom.todayY != null && (
                  <>
                    <line x1={geom.todayX} x2={geom.todayX} y1={geom.todayY - 4} y2={BASE_Y} className="stroke-green-500" strokeWidth={2} strokeDasharray="3 3" />
                    <circle cx={geom.todayX} cy={geom.todayY} r={7} className="fill-green-500 stroke-white dark:stroke-black" strokeWidth={2} />
                    <text x={geom.todayX} y={geom.todayY - 12} textAnchor="middle" className="fill-green-600 dark:fill-green-500" fontSize={11} fontWeight={700} fontFamily="ui-monospace, monospace">{dayLabel}</text>
                  </>
                )}
                {geom.ticks.map((t, i) => (
                  <text key={i} x={t.x} y={CHART_H - 14} textAnchor="middle" className="fill-gray-400 dark:fill-gray-500" fontSize={10} fontFamily="ui-monospace, monospace">{t.label}</text>
                ))}
              </svg>
              <div className="mt-1 flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-gray-400 dark:bg-gray-500" />a past day</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-rp-amber" />most-similar days</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-green-500" />{dayLabel}</span>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-rp-separator bg-gray-50 dark:bg-rp-surface-sunken p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Days most like {dayLabel === "today" ? "today" : dayLabel}
              </p>
              <div className="mt-2">
                {nearest.length > 0 ? (
                  nearest.map((n) => (
                    <div key={n.date} className="flex justify-between border-b border-gray-100 dark:border-gray-800 py-1 font-mono text-xs last:border-b-0">
                      <span className="text-gray-700 dark:text-gray-300">{formatDayLabel(n.date)}</span>
                      <span className="text-rp-amber">{formatSignedPercentage(n.activePct, 2)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">No comparable gap for this day yet.</p>
                )}
              </div>
            </div>
          </div>

          {/* The math, kept out of the headline but available here. */}
          {moveContext.sigma != null && moveContext.trackingErrorPct != null && (
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              {dayLabel === "today" ? "Today's" : `${dayLabel}'s`} gap of{" "}
              <span className="text-gray-700 dark:text-gray-300">{formatSignedPercentage(today as number, 2)}</span>{" "}
              is <span className="text-gray-700 dark:text-gray-300">{formatNumberFixed(Math.abs(moveContext.sigma), 1)}x</span> your typical daily gap of about{" "}
              <span className="text-gray-700 dark:text-gray-300">±{formatNumberFixed(moveContext.trackingErrorPct, 1)}%</span> vs the benchmark.
            </p>
          )}
          </>
        )}
      </div>
    </div>
  );
}
